import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import {
  Accordion,
  AccordionDetails,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { AccordionSummaryWithSpacing } from "./styled/AccordionSummaryWithSpacing";
import { AnalyzedPlan } from "@labs/shared/types/AnalyzedPlan";
import { Target } from "@labs/shared/types/Target";
import { planComponentCategoryLabelMap } from "@labs/shared/data/planComponentCategoryLabelMap.ts";

const TableCellIndent = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 7, 2, 2),
}));

const TableRowLastNoBorder = styled(TableRow)(() => ({
  "&:last-child td, &:last-child th": { border: 0 },
}));

type CarbonImpactTableProps = {
  analyzedPlan: AnalyzedPlan;
  target: Target;
};

enum GapOutcome {
  Ahead = "Ahead",
  OffTrack = "Off Track",
  OnTrack = "On Track",
}

export const CarbonImpactTable = ({
  analyzedPlan,
  target,
}: CarbonImpactTableProps) => {
  const bau = Math.round(
    100 + analyzedPlan.businessGrowth + analyzedPlan.cleanerGrid,
  );
  const businessAsUsualRows = [
    { name: "Baseline Year", carbonUnits: 100 },
    {
      name: "Business Growth",
      carbonUnits: Math.round(analyzedPlan.businessGrowth),
    },
    { name: "Cleaner Grid", carbonUnits: Math.round(analyzedPlan.cleanerGrid) },
  ];
  const plannedImpactRows = analyzedPlan.planComponents.map(
    (planComponent) => ({
      name: planComponentCategoryLabelMap[planComponent.planComponentCategory],
      carbonUnits: Math.round(planComponent.carbonImpact),
    }),
  );

  const gapOutcome = (() => {
    if (analyzedPlan.gap > 0) return GapOutcome.Ahead;
    else if (analyzedPlan.gap === 0) return GapOutcome.OnTrack;
    else return GapOutcome.OffTrack;
  })();

  return (
    <>
      <Stack
        direction={"row"}
        justifyContent={"flex-end"}
        alignItems={"center"}
        spacing={1}
      >
        <Typography variant={"body2"} color={"primary.dark"}>
          Units of Carbon
        </Typography>
        <InfoOutlinedIcon sx={{ color: "action.active" }} />
      </Stack>
      <Accordion>
        <AccordionSummaryWithSpacing expandIcon={<ExpandMoreIcon />}>
          <Typography variant={"body1"}>Business as Usual</Typography>
          <Typography variant={"body1"} justifySelf={"flex-end"}>
            {bau}
          </Typography>
        </AccordionSummaryWithSpacing>
        <AccordionDetails sx={{ paddingTop: 0 }}>
          <Table aria-label="Business as Usual calculations">
            <TableBody>
              {businessAsUsualRows.map((row) => (
                <TableRowLastNoBorder key={row.name}>
                  <TableCell component="td" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCellIndent align="right">
                    {row.carbonUnits}
                  </TableCellIndent>
                </TableRowLastNoBorder>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummaryWithSpacing expandIcon={<ExpandMoreIcon />}>
          <Stack direction={"row"} spacing={1}>
            <Typography variant={"body1"}>Planned Impact</Typography>
            {analyzedPlan.carbonImpact > 0 ? (
              <ArrowUpwardIcon sx={{ color: red[600] }} />
            ) : (
              <ArrowDownwardIcon sx={{ color: green[600] }} />
            )}
          </Stack>

          <Typography variant={"body1"} justifySelf={"flex-end"}>
            {Math.round(analyzedPlan.carbonImpact)}
          </Typography>
        </AccordionSummaryWithSpacing>
        <AccordionDetails sx={{ paddingTop: 0 }}>
          <Table aria-label="Planned Impact calculations">
            <TableBody>
              {plannedImpactRows.map((row) => (
                <TableRowLastNoBorder key={row.name}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCellIndent align="right">
                    {row.carbonUnits}
                  </TableCellIndent>
                </TableRowLastNoBorder>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded={true}>
        <AccordionSummaryWithSpacing expandIcon={<ExpandMoreIcon />}>
          <Typography variant={"body1"}>Remaining Emissions</Typography>
          <Typography variant={"body1"} justifySelf={"flex-end"}>
            {Math.round(analyzedPlan.emissionsRemainingAfterPlan)}
          </Typography>
        </AccordionSummaryWithSpacing>
        <AccordionDetails sx={{ paddingTop: 0 }}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Stack direction={"row"} alignItems={"baseline"} spacing={1}>
              <Typography variant={"body1"}>Gap to Target</Typography>
              {gapOutcome === GapOutcome.OffTrack && (
                <Chip label={"Off Track"} sx={{ backgroundColor: red[100] }} />
              )}
              {gapOutcome === GapOutcome.OnTrack && (
                <Chip label={"On Track"} sx={{ backgroundColor: green[100] }} />
              )}
              {gapOutcome === GapOutcome.Ahead && (
                <Chip label={"Ahead"} sx={{ backgroundColor: green[100] }} />
              )}
            </Stack>

            <Typography
              variant={"body1"}
              justifySelf={"flex-end"}
              sx={{
                color:
                  gapOutcome === GapOutcome.OffTrack ? red[600] : green[600],
              }}
            >
              {Math.round(analyzedPlan.gap)}
            </Typography>
          </Stack>
          <Typography variant={"body2"}>
            Based on your reduction target of {target.goal}%, your{" "}
            {target.targetYear} target is {100 - target.goal} units of carbon.
            This plan is{" "}
            {gapOutcome === GapOutcome.OffTrack && (
              <Box component={"span"} sx={{ color: red[600] }}>
                {Math.round(analyzedPlan.gap)} units off track
              </Box>
            )}
            {gapOutcome === GapOutcome.OnTrack && (
              <Box component={"span"} sx={{ color: green[600] }}>
                on track
              </Box>
            )}
            {gapOutcome === GapOutcome.Ahead && (
              <Box component={"span"} sx={{ color: green[600] }}>
                {Math.round(analyzedPlan.gap)} units ahead of your goal
              </Box>
            )}
            .
          </Typography>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
